import ShareApi from '../apis/ShareApi'

/**
 * Splits an array into chunks of the given size.
 */
export function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize))
  }
  return chunks
}

/**
 * Execute an API call that takes an array of IDs,
 * splitting into chunks and running sequentially.
 */
export async function executeBulkInChunks<T>(
  ids: string[],
  chunkSize: number,
  apiFn: (chunkIds: string[]) => Promise<T>
): Promise<T[]> {
  const chunks = chunkArray(ids, chunkSize)
  const results: T[] = []
  for (const chunk of chunks) {
    const result = await apiFn(chunk)
    results.push(result)
  }
  return results
}

/**
 * Execute individual API calls in chunked batches.
 * Each batch runs in parallel (Promise.all), batches run sequentially.
 */
export async function executeIndividualInChunks<TItem, TResult>(
  items: TItem[],
  chunkSize: number,
  apiFn: (item: TItem) => Promise<TResult>
): Promise<TResult[]> {
  const chunks = chunkArray(items, chunkSize)
  const results: TResult[] = []
  for (const chunk of chunks) {
    const chunkResults = await Promise.all(chunk.map(item => apiFn(item)))
    results.push(...chunkResults)
  }
  return results
}

/**
 * Chunk share/set requests by resourceIds (max 100 per request by default).
 */
export function executeShareSetInChunks<T>(
  scope: string,
  sourceAccountId: string,
  resourceIds: string[],
  targetAccountId: string,
  chunkSize = 100
): Promise<T[]> {
  return executeBulkInChunks(resourceIds, chunkSize, (chunk) =>
    ShareApi.shareSet(scope, sourceAccountId, chunk, targetAccountId)
  )
}

/**
 * Chunk share/unset requests by resourceIds (max 100 per request by default).
 */
export function executeShareUnsetInChunks<T>(
  scopes: string[],
  sourceAccountId: string,
  resourceIds: string[],
  chunkSize = 100
): Promise<T[]> {
  return executeBulkInChunks(resourceIds, chunkSize, (chunk) =>
    ShareApi.shareUnset(scopes, sourceAccountId, chunk)
  )
}
